import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { MediaLibrary, MediaResource, RenderedSlide, ThemeAlign, ThemeDetail, ThemeFont, ThemePosition, ThemeSize } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { SlideView } from './SlideView';
import { BACKGROUNDS, presetUrl } from './backgrounds';

/** ¿Color claro? (para elegir letra oscura o clara sobre un fondo). */
const isLight = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  return ((n >> 16) & 255) * 0.299 + ((n >> 8) & 255) * 0.587 + (n & 255) * 0.114 > 150;
};

interface Draft {
  font: ThemeFont;
  size: ThemeSize;
  textColor: string;
  bgColor: string;
  uppercase: boolean;
  position: ThemePosition;
  align: ThemeAlign;
  backgroundId: string | null;
  backgroundPreset: string | null;
  logoId: string | null;
}

/** Estilos listos: un clic y ya se ve bien. Lo demás se ajusta en «Letra y posición». */
const PRESETS: { name: string; theme: Pick<Draft, 'font' | 'textColor' | 'bgColor' | 'uppercase'> }[] = [
  { name: 'Clásico', theme: { font: 'clasica', textColor: '#ffffff', bgColor: '#000000', uppercase: false } },
  { name: 'Noche', theme: { font: 'elegante', textColor: '#f7f4ec', bgColor: '#1f3440', uppercase: false } },
  { name: 'Claro', theme: { font: 'clasica', textColor: '#1f3440', bgColor: '#f7f4ec', uppercase: false } },
  { name: 'Alabanza', theme: { font: 'fuerte', textColor: '#ffffff', bgColor: '#000000', uppercase: true } },
];

const FONT_LABEL: Record<ThemeFont, string> = { clasica: 'Clásica', elegante: 'Elegante', fuerte: 'Fuerte', moderna: 'Moderna', manuscrita: 'Manuscrita' };
const SIZE_LABEL: Record<ThemeSize, string> = { chica: 'Chica', normal: 'Normal', grande: 'Grande', enorme: 'Muy grande' };
const POSITION_LABEL: Record<ThemePosition, string> = { arriba: 'Arriba', centro: 'Al centro', abajo: 'Abajo' };
const ALIGN_LABEL: Record<ThemeAlign, string> = { izquierda: 'Izquierda', centro: 'Centro', derecha: 'Derecha' };
const keys = <K extends string>(r: Record<K, string>) => Object.keys(r) as K[];

const SAMPLE: RenderedSlide = { item: 1, slide: 0, kind: 'lyrics', title: 'Ejemplo', text: 'Cuán grande es Él\nCuán grande es Él', caption: null, resource: null };

/** Página «Tema de las pantallas» (desde la lista de Proyectar). */
export function ThemePage() {
  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/presentaciones">← Proyectar</Link>
      </p>
      <header className="page__header">
        <h1>Tema de las pantallas</h1>
        <p className="muted">Elige cómo se ven los cantos y la Biblia. Vale para todas las presentaciones y las pantallas abiertas cambian solas.</p>
      </header>
      <ThemeEditor />
    </div>
  );
}

/**
 * Tema de las pantallas: la iglesia lo elige una vez y vale para todas las presentaciones. Cada
 * salida lo adapta sola: la congregación lo ve completo, OBS usa su letra abajo y el escenario
 * sigue en blanco sobre negro para leerse de lejos. Se usa en «Tema de las pantallas» y en la
 * Configuración de la app KEORA Multimedia.
 */
export function ThemeEditor({ onSaved }: { onSaved?: () => void } = {}) {
  const me = useMe();
  const churchId = me.activeChurchId;
  const canOperate = me.can('presentation.operate');
  const url = `/presentation/churches/${churchId}/theme`;
  const theme = useAsync((signal) => (churchId ? api.get<ThemeDetail>(url, signal) : Promise.reject(new Error('Sin contexto'))), [url]);
  const images = useAsync(
    (signal) => (churchId && me.can('media.read') ? api.get<MediaLibrary>(`/media/churches/${churchId}/resources`, signal).then((r) => r.items.filter((x) => x.kind === 'image' && !x.archived)) : Promise.resolve([] as MediaResource[])),
    [churchId],
  );
  const [draft, setDraft] = useState<Draft | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const loaded = theme.status === 'ready' ? theme.data : null;
  useEffect(() => {
    if (loaded) setDraft({ font: loaded.font, size: loaded.size, textColor: loaded.textColor, bgColor: loaded.bgColor, uppercase: loaded.uppercase, position: loaded.position ?? 'centro', align: loaded.align ?? 'centro', backgroundId: loaded.background?.id ?? null, backgroundPreset: loaded.backgroundPreset ?? null, logoId: loaded.logo?.id ?? null });
  }, [loaded]);

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  if (theme.status === 'error') return <ErrorState error={theme.error} onRetry={theme.reload} />;
  if (!loaded || !draft) return <Loading />;

  const t = loaded;
  const set = (patch: Partial<Draft>) => {
    setSaved(false);
    setDraft({ ...draft, ...patch });
  };
  const fileUrl = (id: string) => `/api/media/churches/${churchId}/resources/${id}/file`;
  const pics = images.status === 'ready' ? images.data : [];
  const ref = (id: string | null) => {
    const r = id ? pics.find((x) => x.id === id) : undefined;
    return r ? { id: r.id, kind: r.kind, mime: r.mime, name: r.name } : id ? { id, kind: 'image' as const, mime: 'image/png', name: '' } : null;
  };
  const preview = { ...draft, background: ref(draft.backgroundId) };
  const logoId = draft.logoId ?? t.siteLogoId;
  const active = PRESETS.find((p) => p.theme.font === draft.font && p.theme.textColor === draft.textColor && p.theme.bgColor === draft.bgColor && p.theme.uppercase === draft.uppercase);

  // El logo se sube aquí mismo a Multimedia (también desde la app, donde no se suben otros archivos).
  async function uploadLogo(file: File) {
    setUploading(true);
    setError(null);
    try {
      const r = await api.upload<{ id: string }>(`/media/churches/${churchId}/resources`, file);
      images.reload();
      set({ logoId: r.id });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.put<ThemeDetail>(url, draft);
      setSaved(true);
      theme.reload();
      onSaved?.();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error ? <Alert>{error}</Alert> : null}
      {saved ? <Alert tone="success">Tema guardado.</Alert> : null}
      <div className="theme-previews">
        <figure className="monitor">
          <figcaption>Así se verá</figcaption>
          <SlideView slide={SAMPLE} fileUrl={fileUrl} theme={preview} label="Vista previa del tema" />
        </figure>
        <figure className="monitor">
          <figcaption>Botón Logo</figcaption>
          <SlideView slide={null} fileUrl={fileUrl} theme={preview} logo={{ resourceId: logoId, name: t.churchName }} label="Vista previa del logo" />
        </figure>
      </div>
      {canOperate ? (
        <>
          <Card title="Estilo">
            <div className="theme-presets" role="radiogroup" aria-label="Estilo">
              {PRESETS.map((p) => (
                <button key={p.name} type="button" role="radio" aria-checked={active === p} className={`theme-preset${active === p ? ' theme-preset--active' : ''}`} style={{ background: p.theme.bgColor, color: p.theme.textColor }} onClick={() => set(p.theme)}>
                  {p.name}
                </button>
              ))}
            </div>
          </Card>
          <Card title="Fondo y logo">
            <div className="form">
              <p className="arrangement__label">Fondos KEORA (listos para usar)</p>
              <div className="bg-picker" role="radiogroup" aria-label="Fondos KEORA">
                <button type="button" role="radio" aria-checked={!draft.backgroundPreset && !draft.backgroundId} className="bg-picker__item" style={{ background: draft.bgColor }} onClick={() => set({ backgroundPreset: null, backgroundId: null })}>
                  <span>Solo el color</span>
                </button>
                {BACKGROUNDS.map((b) => (
                  <button
                    key={b.key}
                    type="button"
                    role="radio"
                    aria-checked={draft.backgroundPreset === b.key && !draft.backgroundId}
                    className="bg-picker__item"
                    // Un fondo claro pide letra oscura y viceversa.
                    onClick={() => set({ backgroundPreset: b.key, backgroundId: null, textColor: b.light ? '#1f3440' : isLight(draft.textColor) ? draft.textColor : '#ffffff' })}
                  >
                    <img src={presetUrl(b.key)} alt="" loading="lazy" />
                    <span>{b.name}</span>
                  </button>
                ))}
              </div>
              <label htmlFor="theme-bg">O una imagen de Multimedia</label>
              <select id="theme-bg" value={draft.backgroundId ?? ''} onChange={(e) => set({ backgroundId: e.target.value || null })}>
                <option value="">Ninguna</option>
                {pics.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <label htmlFor="theme-logo">Logo</label>
              <select id="theme-logo" value={draft.logoId ?? ''} onChange={(e) => set({ logoId: e.target.value || null })}>
                <option value="">{t.siteLogoId ? 'El logo del sitio de la iglesia' : 'Sin logo (se muestra el nombre)'}</option>
                {pics.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <label className="btn btn--secondary">
                <span>{uploading ? 'Subiendo…' : 'Subir una imagen para el logo'}</span>
                <input type="file" className="visually-hidden" accept="image/png,image/jpeg,image/webp,image/gif" disabled={uploading} onChange={(e) => e.target.files?.[0] && void uploadLogo(e.target.files[0])} />
              </label>
              <p className="small muted">Es lo que sale al tocar «Logo» en la presentación. Las demás imágenes se suben en <Link to="/app/multimedia">Multimedia</Link>.</p>
            </div>
          </Card>
          <Card title="Letra y posición">
            <div className="form">
              <div className="theme-fields">
                <label>
                  Fuente
                  <select value={draft.font} onChange={(e) => set({ font: e.target.value as ThemeFont })}>
                    {keys(FONT_LABEL).map((k) => (
                      <option key={k} value={k}>
                        {FONT_LABEL[k]}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Tamaño
                  <select value={draft.size} onChange={(e) => set({ size: e.target.value as ThemeSize })}>
                    {keys(SIZE_LABEL).map((k) => (
                      <option key={k} value={k}>
                        {SIZE_LABEL[k]}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Posición
                  <select value={draft.position} onChange={(e) => set({ position: e.target.value as ThemePosition })}>
                    {keys(POSITION_LABEL).map((k) => (
                      <option key={k} value={k}>
                        {POSITION_LABEL[k]}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Alineación
                  <select value={draft.align} onChange={(e) => set({ align: e.target.value as ThemeAlign })}>
                    {keys(ALIGN_LABEL).map((k) => (
                      <option key={k} value={k}>
                        {ALIGN_LABEL[k]}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="theme-colors">
                <label>
                  Color de la letra <input type="color" value={draft.textColor} onChange={(e) => set({ textColor: e.target.value })} />
                </label>
                <label>
                  Color de fondo <input type="color" value={draft.bgColor} onChange={(e) => set({ bgColor: e.target.value })} />
                </label>
              </div>
              <label className="check">
                <input type="checkbox" checked={draft.uppercase} onChange={(e) => set({ uppercase: e.target.checked })} /> Todo en mayúsculas
              </label>
            </div>
          </Card>
          <div className="actions">
            <Button busy={busy} onClick={() => void save()}>
              Guardar tema
            </Button>
          </div>
        </>
      ) : (
        <p className="muted small">Solo quien opera presentaciones puede cambiar el tema.</p>
      )}
    </>
  );
}
